/**
 * Hook for inserting clauses directly into the Word document as Content Controls.
 *
 * Content Controls are tagged with `kobsign:clause:{id}:v{version}` to allow
 * tracking and future updates. Green color (#10B981) distinguishes them from
 * comment CCs (blue).
 */

import { useState, useCallback } from 'react'
import { devLog } from '../services/devLog'
import type { Clause } from '../services/clausesApi'

const CLAUSE_CC_PREFIX = 'kobsign:clause:'
const CLAUSE_CC_COLOR = '#10B981'

export function useClauseInsert() {
  const [isInserting, setIsInserting] = useState(false)

  const insertClause = useCallback(async (clause: Clause): Promise<boolean> => {
    setIsInserting(true)
    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection()
        selection.load('text')
        await context.sync()

        // Insert clause text at cursor position
        const range = selection.insertText(clause.body, Word.InsertLocation.replace)

        // Wrap in Content Control for tracking
        const cc = range.insertContentControl()
        cc.tag = `${CLAUSE_CC_PREFIX}${clause.id}:v${clause.version}`
        cc.title = `${clause.title} (v${clause.version})`
        cc.cannotDelete = true

        // Best-effort styling (requires WordApi 1.3+)
        try {
          cc.color = CLAUSE_CC_COLOR
          cc.appearance = Word.ContentControlAppearance.boundingBox
        } catch {
          // Older Word versions — styling not critical
        }

        await context.sync()
      })

      devLog.info('ClauseInsert', `Inserted clause ${clause.id} v${clause.version}`)
      return true
    } catch (err) {
      devLog.warn('ClauseInsert', `Failed to insert clause: ${err}`)
      return false
    } finally {
      setIsInserting(false)
    }
  }, [])

  return { insertClause, isInserting }
}
